import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * FAQAccordion — native <details>/<summary> (indexable, no JS needed for Google
 * to read the answers) plus optional FAQPage JSON-LD.
 *
 * Props:
 *  - items: [{ q: string, a: string }]
 *  - emitSchema?: boolean          If true, emits FAQPage JSON-LD. Default true.
 *  - testIdPrefix?: string         Default "faq"
 */
const FAQAccordion = ({ items = [], emitSchema = true, testIdPrefix = "faq" }) => {
  if (!items.length) return null;

  const jsonLd = emitSchema
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }
    : null;

  return (
    <>
      {jsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        </Helmet>
      )}
      <div className="space-y-4" data-testid={`${testIdPrefix}-list`}>
        {items.map((item, i) => (
          <details
            key={i}
            className="glass-card rounded-xl px-5 py-4 group"
            data-testid={`${testIdPrefix}-${i}`}
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
              <span className="text-white font-semibold text-[15px]">{item.q}</span>
              <span
                className="text-[color:var(--lt-blue-bright)] text-xl leading-none transition-transform group-open:rotate-45"
                aria-hidden="true"
              >
                +
              </span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-[color:var(--lt-text-dim)]">
              {item.a}
            </p>
          </details>
        ))}
      </div>
    </>
  );
};

export default FAQAccordion;
